/**
 * SiteGlue sitewide CTA hierarchy — single source of truth for labels and destinations.
 *
 * Primary signup: Get Started Free
 * Product exploration (legacy link): liveDemo → /agents on Inbound GPT
 * SiteGlue secondary hero CTA: Book a Demo (opens marketing chat)
 * Personalized onboarding: Start with Trish
 * Guided sales: Book a Demo
 * Enterprise: Contact Sales
 */

import {
  MARKETING_CLONE_AGENTS,
  SITEGLUE_ONBOARD_WORKSPACE_NEW,
} from "~/lib/inbound-gpt/paths";
import { CLERK_AUTH_APP_ORIGIN, marketingAppDashboardUrl } from "~/lib/auth-paths";
import {
  appendAttributionToUrl,
  getAttributionAnalyticsPayload,
} from "~/lib/siteglue/marketing-attribution";
import type { AiEmployeePlanId } from "~/lib/siteglue/ai-employee-plans";
import { serializeChannelsParam } from "~/lib/siteglue/plan-feature-gates";
import {
  serializeProblemsParam,
  type BusinessProblem,
} from "~/lib/siteglue/homepage-onboarding-intake";
import { trackSiteGlueXFromMarketingEvent } from "~/lib/siteglue/x-pixel";

/** Live AI employee demo gallery. Change here to retarget all “See SiteGlue in Action” CTAs. */
export const SITEGLUE_LIVE_DEMO_GALLERY_ROUTE = MARKETING_CLONE_AGENTS; // currently /agents

/**
 * Canonical SiteGlue onboarding destination (absolute).
 * Always includes ?new=1 so Get Started Free / marketing CTAs start a fresh session
 * (onboard session lives in localStorage and survives Safari history clear).
 * Prefer getSiteGlueTrishHref() in client components so brand/campaign params stay correct.
 */
export const SITEGLUE_TRISH_ONBOARDING_URL =
  `${CLERK_AUTH_APP_ORIGIN}${SITEGLUE_ONBOARD_WORKSPACE_NEW}&brand=siteglue` as const;

export type SiteGlueCtaAction =
  | "signup"
  | "liveDemo"
  | "trish"
  | "bookDemo"
  | "contactSales"
  | "signIn"
  | "comparePlans"
  | "discoverPlatform"
  | "exploreLiveEmployees"
  | "launchLiveDemo"
  | "scrollLiveDemo"
  | "scanSite";

export type SiteGlueCtaLocation =
  | "header"
  | "mobile_menu"
  | "hero"
  | "homepage_supporting_link"
  | "homepage_trish_section"
  | "pricing_card"
  | "pricing_hero"
  | "pricing_final"
  | "pricing_advisor"
  | "feature_section"
  | "demo_card"
  | "footer"
  | "final_cta"
  | "industry_hero"
  | "industry_final"
  | "industry_page"
  | "role_page"
  | "empty_state"
  | "blog_sidebar"
  | "blog_inline"
  | "blog_inline_callout"
  | "blog_footer"
  | "blog_related"
  | "blog_newsletter"
  | "blog_employee_card"
  | "faq_footer"
  | "how_it_works"
  | "about_supporting"
  | "ai_summary";

/** Optional context appended only when the onboarding app recognizes the key. */
export type SiteGlueTrishContext = {
  role?: string;
  industry?: string;
  page?: string;
  campaign?: string;
  referrer?: string;
  handoff?: string;
  scanId?: string;
  domain?: string;
  selectedFixIds?: string;
  website?: string;
  services?: string;
  channels?: string;
  wp?: string;
  totalCents?: string;
  plan?: string;
  audit?: string;
  score?: string;
};

/**
 * Query keys recognized by /onboard (and legacy hire builder).
 * Do not append role/industry until the onboard app reads them.
 */
const TRISH_SUPPORTED_CONTEXT_KEYS = [
  "campaign",
  "referrer",
  "handoff",
  "scanId",
  "domain",
  "selectedFixIds",
  "website",
  "services",
  "channels",
  "wp",
  "totalCents",
  "plan",
  "audit",
  "score",
] as const;

export const SITEGLUE_SIGNUP_CTA_LABEL = "Get Started" as const;

export const SITEGLUE_CTA_CONFIG = {
  signup: {
    label: SITEGLUE_SIGNUP_CTA_LABEL,
    /** Resolved at click/render via getSignupHref() so brand param stays correct. */
    hrefKey: "signup" as const,
    analyticsEvent: "cta_signup_clicked",
  },
  liveDemo: {
    label: "Explore Live Agents",
    href: SITEGLUE_LIVE_DEMO_GALLERY_ROUTE,
    analyticsEvent: "cta_live_demo_clicked",
  },
  trish: {
    label: "Get Started",
    hrefKey: "trish" as const,
    analyticsEvent: "cta_trish_started",
  },
  bookDemo: {
    label: "Book a Demo",
    /** Opens marketing chat demo flow (existing SiteGlue booking path). */
    hrefKey: "bookDemo" as const,
    analyticsEvent: "cta_book_demo_clicked",
  },
  contactSales: {
    label: "Contact Sales",
    hrefKey: "contactSales" as const,
    analyticsEvent: "cta_contact_sales_clicked",
  },
  signIn: {
    label: "Sign In",
    hrefKey: "signIn" as const,
    analyticsEvent: "cta_sign_in_clicked",
  },
  comparePlans: {
    label: "Compare Plans",
    href: "/pricing#pricing",
    analyticsEvent: "cta_compare_plans_clicked",
  },
  discoverPlatform: {
    label: "Discover the Platform",
    href: "/platform",
    analyticsEvent: "cta_discover_platform_clicked",
  },
  exploreLiveEmployees: {
    label: "Explore Live Agents",
    href: SITEGLUE_LIVE_DEMO_GALLERY_ROUTE,
    analyticsEvent: "cta_explore_live_employees_clicked",
  },
  launchLiveDemo: {
    label: "Launch Live Demo",
    hrefKey: "launchLiveDemo" as const,
    analyticsEvent: "cta_live_agent_launched",
  },
  scrollLiveDemo: {
    label: "Live Demo",
    href: "#live-demo",
    analyticsEvent: "cta_live_demo_scroll_clicked",
  },
  scanSite: {
    label: "Scan your site",
    href: "/agent-ready",
    analyticsEvent: "cta_scan_site_clicked",
  },
} as const;

/** Alias matching the recommended public API in marketing docs. */
export const CTA_CONFIG = SITEGLUE_CTA_CONFIG;

export const SITEGLUE_PLAN_CTA_LABELS = {
  intern: "Start 15-day trial",
  associate: "Buy Now",
  professional: "Buy Now",
  enterprise: "Contact Sales",
} as const;

/** Standard Trish onboarding card copy (marketing surfaces). */
export const TRISH_CARD_COPY = {
  eyebrow: "MEET TRISH",
  heading: "Trish",
  role: "SiteGlue Onboarding Specialist",
  body: "Trish sets up your AI agent from your website and the channels you choose, then walks you through a guided review—profile, locations, team, and channels—so you can go live with Always a Human-in-the-Loop™.",
  supporting: "Go from your website to a working AI agent.",
  cta: SITEGLUE_CTA_CONFIG.trish.label,
} as const;

/** Get Started Free → SiteGlue /onboard (fresh session). */
export function getSiteGlueSignupHref(): string {
  return appendAttributionToUrl(SITEGLUE_TRISH_ONBOARDING_URL);
}

/**
 * Pricing / plan-card Select → Trish onboarding with the chosen plan pre-applied.
 * Uses SiteGlue plan ids (intern|associate|professional) in ?plan=.
 */
export function getSiteGluePlanOnboardingHref(
  planId: Exclude<AiEmployeePlanId, "enterprise">,
): string {
  try {
    const url = new URL(SITEGLUE_TRISH_ONBOARDING_URL);
    url.searchParams.set("plan", planId);
    return appendAttributionToUrl(url.href);
  } catch {
    const joiner = SITEGLUE_TRISH_ONBOARDING_URL.includes("?") ? "&" : "?";
    return appendAttributionToUrl(`${SITEGLUE_TRISH_ONBOARDING_URL}${joiner}plan=${planId}`);
  }
}

/**
 * Get Started / homepage form → Trish onboarding with ?website= prefilled.
 * Optional problems + channels for outcome-based recommendation (no ?plan= auto-confirm).
 */
export function getSiteGlueOnboardingHrefWithWebsite(
  websiteUrl: string,
  options?: {
    channels?: string[];
    problems?: BusinessProblem[];
    role?: string;
    roleLabel?: string;
    company?: string;
    approved?: boolean;
    /** Role-setup channel ids chosen in the hero, before the website question. */
    roleChannels?: string[];
  },
): string {
  try {
    const url = new URL(SITEGLUE_TRISH_ONBOARDING_URL);
    url.searchParams.set("website", websiteUrl);
    const channels = options?.channels?.filter(Boolean) ?? [];
    const problems = options?.problems?.filter(Boolean) ?? [];
    if (channels.length) {
      url.searchParams.set("channels", serializeChannelsParam(channels));
    }
    if (problems.length) {
      url.searchParams.set("problems", serializeProblemsParam(problems));
    }
    if (options?.role) url.searchParams.set("role", options.role);
    if (options?.roleLabel) url.searchParams.set("roleLabel", options.roleLabel);
    if (options?.company) url.searchParams.set("company", options.company);
    if (options?.approved) url.searchParams.set("approved", "1");
    const roleChannels = options?.roleChannels?.filter(Boolean) ?? [];
    if (roleChannels.length) url.searchParams.set("roleChannels", roleChannels.join(","));
    return appendAttributionToUrl(url.href);
  } catch {
    const joiner = SITEGLUE_TRISH_ONBOARDING_URL.includes("?") ? "&" : "?";
    const channels = options?.channels?.filter(Boolean) ?? [];
    const problems = options?.problems?.filter(Boolean) ?? [];
    let href = `${SITEGLUE_TRISH_ONBOARDING_URL}${joiner}website=${encodeURIComponent(websiteUrl)}`;
    if (channels.length) {
      href += `&channels=${encodeURIComponent(serializeChannelsParam(channels))}`;
    }
    if (problems.length) {
      href += `&problems=${encodeURIComponent(serializeProblemsParam(problems))}`;
    }
    if (options?.role) href += `&role=${encodeURIComponent(options.role)}`;
    if (options?.roleLabel) href += `&roleLabel=${encodeURIComponent(options.roleLabel)}`;
    if (options?.company) href += `&company=${encodeURIComponent(options.company)}`;
    if (options?.approved) href += "&approved=1";
    const roleChannels = options?.roleChannels?.filter(Boolean) ?? [];
    if (roleChannels.length) {
      href += `&roleChannels=${encodeURIComponent(roleChannels.join(","))}`;
    }
    return appendAttributionToUrl(href);
  }
}

export function getSiteGlueLiveDemoGalleryHref(): string {
  return SITEGLUE_LIVE_DEMO_GALLERY_ROUTE;
}

/**
 * Trish / onboard href. Preserves brand=siteglue via marketingAppDashboardUrl.
 * Uses ?new=1 for a fresh /onboard session. Appends only supported optional context params.
 */
export function getSiteGlueTrishHref(context?: SiteGlueTrishContext): string {
  const base = marketingAppDashboardUrl(`${SITEGLUE_ONBOARD_WORKSPACE_NEW}&brand=siteglue`);
  if (!context) return base;

  try {
    const url = new URL(base, typeof window !== "undefined" ? window.location.origin : CLERK_AUTH_APP_ORIGIN);
    for (const key of TRISH_SUPPORTED_CONTEXT_KEYS) {
      const value = context[key];
      if (value) url.searchParams.set(key, value);
    }
    return appendAttributionToUrl(url.href);
  } catch {
    return appendAttributionToUrl(base);
  }
}

/** Push a SiteGlue CTA event to GA4 / GTM dataLayer when available. */
export function trackSiteGlueCta(params: {
  action: SiteGlueCtaAction;
  label: string;
  location: SiteGlueCtaLocation;
  destination?: string;
  extra?: Record<string, string | undefined>;
}): void {
  if (typeof window === "undefined") return;
  const pagePath = window.location.pathname;
  const configEntry = SITEGLUE_CTA_CONFIG[params.action];
  const eventName =
    params.extra?.event ??
    ("analyticsEvent" in configEntry ? configEntry.analyticsEvent : `cta_${params.action}_clicked`);
  const payload = {
    event: eventName,
    cta_action: params.action,
    cta_label: params.label,
    cta_location: params.location,
    page_path: pagePath,
    destination: params.destination ?? "",
    ...getAttributionAnalyticsPayload(),
    ...params.extra,
  };
  try {
    const w = window as Window & {
      dataLayer?: Record<string, unknown>[];
      gtag?: (...args: unknown[]) => void;
    };
    w.dataLayer = w.dataLayer ?? [];
    w.dataLayer.push(payload);
    if (typeof w.gtag === "function") {
      w.gtag("event", eventName, payload);
    }
  } catch {
    // Analytics must never block UX
  }
}

/** Generic SiteGlue marketing event (role pages, FAQ, channels, integrations). */
export function trackSiteGlueEvent(
  eventName: string,
  extra?: Record<string, string | number | boolean | undefined>,
): void {
  if (typeof window === "undefined") return;
  const pagePath = window.location.pathname;
  const payload = {
    event: eventName,
    page_path: pagePath,
    ...getAttributionAnalyticsPayload(),
    ...extra,
  };
  try {
    const w = window as Window & {
      dataLayer?: Record<string, unknown>[];
      gtag?: (...args: unknown[]) => void;
    };
    w.dataLayer = w.dataLayer ?? [];
    w.dataLayer.push(payload);
    if (typeof w.gtag === "function") {
      w.gtag("event", eventName, payload);
    }
  } catch {
    // Analytics must never block UX
  }
  // Native X pixel (signup / hire / paid landing) — not GTM-dependent.
  trackSiteGlueXFromMarketingEvent(eventName, extra);
}
